import React, { Fragment, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Stack,
    Grid,
    Divider,
} from '@mui/material';
import {
    KeyboardArrowUp as UpIcon,
    KeyboardArrowDown as DownIcon,
    Add,
    Close,
    BorderColor,
    DeleteForever,
} from '@mui/icons-material';
import theme from '../../theme';
import styled from '@emotion/styled';
import CommonButton from '../../common/components/CommonButton';
import CommonDialog from '../../common/components/CommonDialog';
import CommonFormField from '../../common/components/CommonFormField';
import CommonEditor from '../../common/components/CommonEditor';
import RichTextEditor from 'react-rte';
import { showNotification } from '../../common/components/Notification/ToastifyNotification';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../../common/components/LoadingSpinner';
import { setLoading } from '../../features/authSlice';
import { createFaq, deleteFaq, getFaqList, updateFaq, updateReorder } from '../../api/faqApi';

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const ManageFAQ = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const [faqs, setFaqs] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingFaq, setEditingFaq] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [value, setValue] = useState('');
    const [questionError, setQuestionError] = useState('');
    const [answerError, setAnswerError] = useState('');
    const [newFaq, setNewFaq] = useState({
        question: '',
        answer: RichTextEditor?.createValueFromString(value || '', 'html')
    });

    // Validation function for question field
    const validateQuestion = (value) => {
        if (value.trim() === '') {
            setQuestionError('');
            return true;
        }
        
        // Check if the value contains only numbers (no letters)
        const onlyNumbersRegex = /^[0-9\s]+$/;
        if (onlyNumbersRegex.test(value)) {
            setQuestionError('Question cannot contain only numbers. Please add some text.');
            return false;
        }
        
        // Check if value has at least one letter
        const hasLetter = /[A-Za-z]/.test(value);
        if (!hasLetter) {
            setQuestionError('Question must contain at least one letter');
            return false;
        }
        
        setQuestionError('');
        return true;
    };

    // Validation function for answer field
    const validateAnswer = (htmlValue, textValue) => {
        // Remove HTML tags to get plain text
        const plainText = htmlValue?.replace(/<[^>]*>/g, '') || '';
        const trimmedText = plainText.trim();
        
        if (trimmedText === '') {
            setAnswerError('');
            return true;
        }
        
        // Check if the value contains only numbers (no letters)
        const onlyNumbersRegex = /^[0-9\s]+$/;
        if (onlyNumbersRegex.test(trimmedText)) {
            setAnswerError('Answer cannot contain only numbers. Please add some text.');
            return false;
        }
        
        // Check if value has at least one letter
        const hasLetter = /[A-Za-z]/.test(trimmedText);
        if (!hasLetter) {
            setAnswerError('Answer must contain at least one letter');
            return false;
        }
        
        setAnswerError('');
        return true;
    };

    const handleOpenDialog = (faq = null) => {
        setEditingFaq(faq);
        setQuestionError('');
        setAnswerError('');
        setNewFaq({
            question: faq?.question || '',
            answer: RichTextEditor?.createValueFromString(faq?.answer || '', 'html')
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setEditingFaq(null);
        setValue('');
        setQuestionError('');
        setAnswerError('');
        setNewFaq({
            question: '',
            answer: RichTextEditor?.createValueFromString('', 'html')
        });
        setOpenDialog(false);
    };

    const handleSaveFaq = async () => {
        // Get plain text from answer for validation
        const answerHtml = newFaq.answer?._cache?.html || '';
        const plainTextAnswer = answerHtml.replace(/<[^>]*>/g, '');
        
        // Additional validation before saving
        if (!newFaq.question.trim()) {
            showNotification('Please enter a question', 'error');
            return;
        }

        if (questionError) {
            showNotification('Please fix the validation errors in question before saving', 'error');
            return;
        }
        
        if (!plainTextAnswer.trim()) {
            showNotification('Please enter an answer', 'error');
            return;
        }
        
        if (answerError) {
            showNotification('Please fix the validation errors in answer before saving', 'error');
            return;
        }

        try {
            if (newFaq.answer.toString('markdown').length > 2) {
                dispatch(setLoading({
                    loading: true
                }));
                if (editingFaq) {
                    const response = await updateFaq(editingFaq.faq_id, {
                        "question": newFaq.question,
                        "answer": newFaq.answer?._cache?.html,
                    });
                    if (response.success) {
                        showNotification(response.message, 'success');
                        dispatch(setLoading({
                            loading: false
                        }));
                        getFaq();
                    }
                } else {
                    const response = await createFaq({
                        "question": newFaq.question,
                        "answer": newFaq.answer?._cache?.html,
                        "sort_order": faqs.length + 1
                    });
                    if (response.success) {
                        showNotification(response.message, 'success');
                        dispatch(setLoading({
                            loading: false
                        }));
                        getFaq();
                    }
                }
                handleCloseDialog();
            } else {
                showNotification('Please enter a answer', 'error');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const handleDelete = async (id) => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await deleteFaq(id);
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setDeleteConfirm(null);
                getFaq();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const handleMove = async (id, direction) => {
        try {
            const index = faqs.findIndex(faq => faq.faq_id === id);
            const newIndex = direction === 'up' ? index - 1 : index + 1;

            if (newIndex >= 0 && newIndex < faqs.length) {
                const newFaqs = [...faqs];
                [newFaqs[index], newFaqs[newIndex]] = [newFaqs[newIndex], newFaqs[index]];
                const updatedFaqs = newFaqs.map((faq, idx) => ({
                    ...faq,
                    sort_order: idx + 1
                }));
                dispatch(setLoading({
                    loading: true
                }));
                const response = await updateReorder(updatedFaqs.map((a) => ({
                    faq_id: a.faq_id,
                    sort_order: a.sort_order
                })));
                if (response.success) {
                    showNotification(response.message, 'success');
                    dispatch(setLoading({
                        loading: false
                    }));
                    getFaq();
                }
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const getFaq = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getFaqList();
            setFaqs(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getFaq();
    }, []);

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography fontSize={20} fontWeight={600}>
                    FAQ Manager
                </Typography>
                <CommonButton
                    label="Add New Question"
                    marginBottom="0px"
                    width="200px"
                    fontWeight={600}
                    startIcon={<Add />}
                    background={theme.palette.secondary.main}
                    color="White"
                    handleClick={() => {
                        setOpenDialog(true);
                    }}
                />
            </Box>
            <TableContainer sx={{ border: '1px solid lightgray' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                            <Cell width="40%">Question</Cell>
                            <Cell width="15%" align="center">Actions</Cell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {faqs.length > 0 ? faqs.map((faq) => (
                            <TableRow
                                key={faq.id}
                            >
                                <TableCell>
                                    <Typography fontWeight="medium" gutterBottom>
                                        {faq.question}
                                    </Typography>
                                    <Typography
                                        component="div"
                                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                        <Tooltip title="Move Up">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleMove(faq.faq_id, 'up')}
                                                    disabled={faq.sort_order === 1}
                                                >
                                                    <UpIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Move Down">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleMove(faq.faq_id, 'down')}
                                                    disabled={faq.sort_order === faqs.length}
                                                >
                                                    <DownIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleOpenDialog(faq)}
                                            >
                                                <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => setDeleteConfirm(faq)}
                                            >
                                                <DeleteForever sx={{ color: 'red', fontSize: 20 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={2} align="center">
                                    <Typography>No FAQ's Found</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <CommonDialog
                open={openDialog}
                size="sm"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    handleCloseDialog();
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.1} mb={1}>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Question *"
                                name="question"
                                value={newFaq.question}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setNewFaq({ ...newFaq, question: newValue });
                                    validateQuestion(newValue);
                                }}
                                placeholder="Enter the question..."
                                error={!!questionError}
                                helperText={questionError}
                            />
                        </Grid>
                        <Grid item md={12}>
                            <CommonEditor
                                placeholder="Enter the answer...."
                                value={newFaq.answer}
                                setValue={(value) => {
                                    const htmlValue = value.toString('html');
                                    setValue(htmlValue);
                                    setNewFaq({ ...newFaq, answer: value });
                                    validateAnswer(htmlValue);
                                }}
                                error={!!answerError}
                                helperText={answerError}
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label={editingFaq ? 'Update FAQ' : 'Save FAQ'}
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        disabled={!newFaq.question.trim() || !!questionError || !!answerError}
                        handleClick={() => {
                            handleSaveFaq();
                        }}
                    />
                )}
            />
            <CommonDialog
                open={!!deleteConfirm}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to delete the question?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                handleDelete(deleteConfirm.faq_id)
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setDeleteConfirm(null)
                            }}
                        />
                    </Fragment>
                )}
            />
        </Fragment>
    );
};

export default ManageFAQ;